import {CatalogConfig} from '../types'

// Phase B1a — the remaining Experience sub-catalogs (Beneficiaries shipped in the pilot).
//
// All four have a full CRUD ModelViewSet (admin-gated writes) but NO soft-delete field yet
// (readiness: NEEDS-FIELD, not MODEL-READY). They are mounted READ-ONLY here (no backend change):
// view-only screens are already useful, and enabling create/edit/deactivate is deferred to Phase
// B2, which adds `is_active` via migration and upgrades these to full CRUD in one coherent change.
// (Open question in the PR: enable create/edit now, since the write APIs exist, vs. wait for B2.)
//
// Endpoints (baseURL already includes /api/v1):
//   categories            experiences.ExperienceCategory        /experiences/categories/        {id,name}
//   audiences             experiences.ExperienceAudience        /experiences/audiences/         {id,name}         (Experience.audience_type is FREE-TEXT, not FK)
//   duration types        experiences.ExperienceDurationType    /experiences/duration-types/    {id,name}         (Experience.duration_type is FREE-TEXT, not FK)
//   confidentiality types experiences.ExperienceConfidentiality /experiences/confidentiality-levels/ {id,name,description} (FK-coupled)

export const experienceCategoriesConfig: CatalogConfig = {
  slug: 'experience-categories',
  endpoint: '/experiences/categories/',
  titleId: 'CATALOG.EXPERIENCE_CATEGORIES.TITLE',
  singularId: 'CATALOG.EXPERIENCE_CATEGORIES.SINGULAR',
  primaryField: 'name',
  writable: true,
  reorderable: true,
  softDelete: {field: 'isActive', activeValue: true},
  serverSide: {
    searchParam: 'search',
    orderingParam: 'ordering',
    statusParam: 'isActive',
    sortableFields: ['order', 'name'],
    defaultOrdering: 'order',
  },
  fields: [
    {
      name: 'order',
      labelId: 'CATALOG.FIELD.ORDER',
      type: 'text',
      showInTable: true,
    },
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
      required: true,
      maxLength: 100,
    },
  ],
}

export const experienceAudiencesConfig: CatalogConfig = {
  slug: 'experience-audiences',
  endpoint: '/experiences/audiences/',
  titleId: 'CATALOG.EXPERIENCE_AUDIENCES.TITLE',
  singularId: 'CATALOG.EXPERIENCE_AUDIENCES.SINGULAR',
  primaryField: 'name',
  writable: false,
  // Server-side pagination — see experienceCategoriesConfig for the searchParam/no-ordering notes.
  serverSide: {searchParam: 'search'},
  fields: [
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
    },
  ],
}

export const experienceDurationsConfig: CatalogConfig = {
  slug: 'experience-durations',
  endpoint: '/experiences/duration-types/',
  titleId: 'CATALOG.EXPERIENCE_DURATIONS.TITLE',
  singularId: 'CATALOG.EXPERIENCE_DURATIONS.SINGULAR',
  primaryField: 'name',
  writable: false,
  // Server-side pagination — see experienceCategoriesConfig for the searchParam/no-ordering notes.
  serverSide: {searchParam: 'search'},
  fields: [
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
    },
  ],
}

export const experienceConfidentialityConfig: CatalogConfig = {
  slug: 'experience-confidentiality',
  endpoint: '/experiences/confidentiality-levels/',
  titleId: 'CATALOG.EXPERIENCE_CONFIDENTIALITY.TITLE',
  singularId: 'CATALOG.EXPERIENCE_CONFIDENTIALITY.SINGULAR',
  primaryField: 'name',
  writable: false,
  // Server-side pagination. searchParam only — the backend orders by `id` (a deliberate
  // confidentiality escalation: No Media -> Limited Media -> Open Media), so no ordering param is
  // sent and that sequence is preserved.
  serverSide: {searchParam: 'search'},
  fields: [
    {
      name: 'name',
      labelId: 'CATALOG.FIELD.NAME',
      type: 'text',
      showInTable: true,
      searchable: true,
    },
    {
      name: 'description',
      labelId: 'CATALOG.FIELD.DESCRIPTION',
      type: 'textarea',
      showInTable: true,
      searchable: true,
    },
  ],
}

export const experienceCatalogConfigs: CatalogConfig[] = [
  experienceCategoriesConfig,
  experienceAudiencesConfig,
  experienceDurationsConfig,
  experienceConfidentialityConfig,
]
